/**
 * OWA-930: user-facing handling of the generated cascading deletes.
 *
 * Every generated `delete<Entity>()` returns `{status, impacts}`:
 * - `deleted`: the delete and its declared children were queued.
 * - `blocked`: a blocker (impact category ending in `*`) stopped it before anything was queued.
 * - `notReady`: this tab has not finished its first fetch of every collection, so the cascade
 *   could miss children; nothing was queued.
 *
 * The helpers here turn those results and the delete-impact counts into the confirmation, refusal
 * and "still loading" messages the pages show, and give components a readiness flag that follows
 * the tab session.
 */
import React, {ReactElement, SyntheticEvent, useSyncExternalStore} from 'react';
import {tabSession} from '@/Auth/session';
import {STILL_LOADING_MESSAGE} from '@/lib/alertMessages';
import {canCascadeDelete} from '@/store/deps/deleteContext';
import type {DeleteImpact, DeleteResult} from '@/store/deps/deleteContext';
// A namespace import, so a test that mocks '@/ui/Confirm' without every dialog still loads this module.
import * as Confirm from '@/ui/Confirm';

export {STILL_LOADING_MESSAGE};
export const STILL_LOADING_TOOLTIP = 'Still loading your data';
export const CANNOT_DELETE_TITLE = 'Cannot delete';

/** Labels whose words do not follow from splitting the collection name. */
const LABELS: Record<string, [singular: string, plural: string]> = {
    matters: ['Case', 'Cases'],
    matterStages: ['Case stage', 'Case stages'],
    caseTypes: ['Case type', 'Case types'],
    caseTypeStages: ['Case type stage', 'Case type stages'],
    timeOffs: ['time off entry', 'time off entries'],
    addresses: ['address', 'addresses'],
    invoiceBatches: ['invoice batch', 'invoice batches'],
};

/**
 * Billing records that block a delete while they sit on an invoice. A discount on an invoice is
 * owned by the invoice, so it never blocks the Case it came from. A task on an invoice is billed
 * work too (OWA-1650).
 */
const BILLED = new Set(['expenses', 'flatFees', 'flatFeeMilestones', 'tasks']);

function words(collection: string): string {
    return collection.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}

function singular(plural: string): string {
    if (plural.endsWith('ies')) return plural.slice(0, -3) + 'y';
    if (/(ch|sh|ss|x)es$/.test(plural)) return plural.slice(0, -2);
    if (plural.endsWith('s')) return plural.slice(0, -1);
    return plural;
}

/** "answers" with count 1 is "answer"; "matterStages" is "Case stages"; a trailing `*` is dropped. */
export function impactLabel(value: string, count = 2): string {
    const collection = value.endsWith('*') ? value.slice(0, -1) : value;
    const known = LABELS[collection];
    if (known) return count === 1 ? known[0] : known[1];
    const plural = words(collection);
    return count === 1 ? singular(plural) : plural;
}

function joinAnd(items: string[]): string {
    if (items.length <= 1) return items.join('');
    return items.slice(0, -1).join(', ') + ' and ' + items.at(-1);
}

export function isBlocker(impact: {value: string}): boolean {
    return impact.value.endsWith('*');
}

/** Sentence-case the first letter for the start of a sentence, keeping "Case" capitalised. */
function sentence(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Why a delete is blocked, in words. A flat fee, flat fee milestone or expense that is itself on
 * an invoice says so. Billed expenses, flat fees and flat fee milestones under the record must come
 * off their invoices first; any other blocker is a record that still needs this one.
 *
 * @param entityLabel - what is being deleted, as the user reads it ("Case", "lead", "court")
 * @param impacts - the delete-impact counts or a blocked result's impacts
 * @param billedChildren - false when the blockers are records that refer to this one rather than
 *     its own billed children: an expense type or discount reason lookup is still used by
 *     expenses or discounts, which are not "billed" because of it
 */
export function blockedReason(entityLabel: string, impacts: DeleteImpact, billedChildren = true): string {
    const blockers = impacts.filter(isBlocker);
    const isBilled = (b: {value: string}) => billedChildren && BILLED.has(b.value.slice(0, -1));
    // A flat fee, flat fee milestone or expense that is itself on an invoice blocks its own delete;
    // its blocker is its own collection, which no other record has as a billed child.
    const isRoot = (b: {value: string}) => isBilled(b) && impactLabel(b.value, 1).toLowerCase() === entityLabel.toLowerCase();
    if (blockers.some(isRoot)) return `This ${entityLabel} is on an invoice. Remove it from the invoice before deleting it.`;
    const billed = blockers.filter(isBilled);
    const other = blockers.filter(b => !isBilled(b));
    const reasons: string[] = [];
    if (billed.length) {
        reasons.push(`This ${entityLabel} has billed ${joinAnd(billed.map(b => impactLabel(b.value)))}. Remove them from their invoices first.`);
    }
    if (other.length) {
        const used = joinAnd(other.map(b => `${b.count} ${impactLabel(b.value, b.count)}`));
        reasons.push(`${billed.length ? 'It' : `This ${entityLabel}`} is still used by ${used}. Change or remove them first.`);
    }
    return reasons.join(' ') || `This ${entityLabel} cannot be deleted.`;
}

/**
 * The delete confirmation for a cascading delete: the counts of records the delete removes or
 * updates, or the reason it cannot go ahead. Matches the `confirmDeleteWarningFn` contract of
 * EditableTable (`canDelete` false shows the message with a single button).
 *
 * @param entityLabel - "Case", "lead", "contact"
 * @param impacts - from `deleteImpact<Entity>()`
 * @param name - optional display name of the record
 */
export function deleteImpactWarning(entityLabel: string, impacts: DeleteImpact, name?: string): {canDelete: boolean, message: ReactElement | null} {
    if (impacts.some(isBlocker)) {
        return {canDelete: false, message: <div data-testid='delete-blocked'>{blockedReason(entityLabel, impacts)}</div>};
    }
    if (!impacts.length) return {canDelete: true, message: null};
    return {
        canDelete: true,
        message: <div data-testid='delete-impact'>
            Deleting this {entityLabel} also deletes or updates these records:
            <ul>
                {impacts.map(impact => <li key={impact.value}>{impact.count} {impactLabel(impact.value, impact.count)}</li>)}
            </ul>
            <span>Are you sure you want to delete {name ? <b>{name}</b> : `this ${entityLabel}`}?</span>
        </div>,
    };
}

/** Add up the impacts of several records, for one confirmation of a multi-select delete. */
export function combineImpacts(impacts: DeleteImpact[]): DeleteImpact {
    const totals = new Map<string, number>();
    impacts.flat().forEach(impact => totals.set(impact.value, (totals.get(impact.value) ?? 0) + impact.count));
    return [...totals].map(([value, count]) => ({value, count}));
}

/**
 * Ask before a cascading delete, or refuse it. When the impacts hold a blocker the dialog gives the
 * reason with a single OK button and `onYes` can never run (OWA-1650); otherwise Yes runs `onYes`.
 * Returns true when the question was asked.
 */
export function confirmCascadeDelete(options: {
    title: string,
    entityLabel: string,
    impacts: DeleteImpact,
    name?: string,
    onYes: (e?: SyntheticEvent) => void,
    onNo?: (e?: SyntheticEvent) => void,
}): boolean {
    const {title, entityLabel, impacts, name, onYes, onNo} = options;
    const {canDelete, message} = deleteImpactWarning(entityLabel, impacts, name);
    if (!canDelete) {
        Confirm.confirmWarning(CANNOT_DELETE_TITLE, message ?? '', 'OK', () => {});
        return false;
    }
    Confirm.confirmWarningOkCancel(
        title,
        message ?? <div>
            <p>About to delete {name ? <strong>{name}</strong> : `this ${entityLabel}`}</p>
            <p>Proceed?</p>
        </div>,
        'Yes', 'No',
        onYes,
        onNo ?? (() => {})
    );
    return true;
}

/**
 * Report a delete that did not happen. Returns true when the delete was queued.
 *
 * @param entityLabel - "Case", "lead"
 * @param result - the generated delete's result
 * @param billedChildren - as for `blockedReason`
 */
export function reportDeleteResult(entityLabel: string, result: DeleteResult, billedChildren = true): boolean {
    if (result.status === 'deleted') return true;
    if (result.status === 'blocked') Confirm.confirmWarning(CANNOT_DELETE_TITLE, blockedReason(entityLabel, result.impacts, billedChildren), 'OK', () => {});
    else if (!notReadyShown) {
        // One action can refuse several deletes (a cancelled card's records); say it once.
        notReadyShown = true;
        queueMicrotask(() => { notReadyShown = false; });
        Confirm.confirmWarning(sentence(STILL_LOADING_TOOLTIP), STILL_LOADING_MESSAGE, 'OK', () => {});
    }
    return false;
}
let notReadyShown = false;

/**
 * True once this tab has fetched every collection a cascading delete reads. Re-renders when the
 * tab session changes (each first fetch of a collection, a new account, an ended session).
 */
export function useCascadeDeleteReady(): boolean {
    useSyncExternalStore(tabSession.subscribe, tabSession.getRevision);
    return canCascadeDelete();
}
